import { saveAs } from "file-saver";

import { ConsumptionExportFormat, type Meter } from "@eisox/api/core-api";
import { Button, DropdownMenu } from "@eisox/design-system";
import { ChevronDownIcon } from "@eisox/icons";
import { useMutation } from "@tanstack/react-query";

import { meterApi } from "~/api";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { consumptionParams, indicatorParams } from "../consumption.queries";
import type { ChartView } from "../types/consumption.type";

const NAME = "ExportMenu";

interface ExportMenuProps {
  meter: Meter;
  view: ChartView;
}

const ExportMenu: React.FC<ExportMenuProps> = ({ meter, view: { period, degreeDays } }) => {
  const { building } = useBuildingContext(NAME);
  const { mutate: exportConsumption, isPending } = useMutation({
    meta: { toast: { loading: m.loading(), success: m.export_success() } },
    mutationFn: (format: ConsumptionExportFormat) =>
      meterApi.exportConsumption(building.id, meter.id, {
        ...consumptionParams(building.timezone, period.granularity, period.range, {
          degreeDays,
          ...indicatorParams(period.granularity),
        }),
        format,
      }),
    onSuccess: (file, format) => saveAs(file, `${meter.name}_${period.range.from}_${period.range.to}.${format}`),
  });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild disabled={isPending}>
        <Button>
          {m.export()}
          <ChevronDownIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        {Object.values(ConsumptionExportFormat).map(format => (
          <DropdownMenu.Item key={format} onSelect={() => exportConsumption(format)}>
            .{format}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

ExportMenu.displayName = NAME;

export { ExportMenu };
